import React from 'react';
import { FaEye, FaPrint, FaEdit, FaTrash, FaUndo, FaCloudUploadAlt, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { formatDate } from '../../../utils/formatters';
import { useTranslation } from '../../../context/LanguageContext';

const OrdersTable = ({
    orders,
    loading,
    formatCurrency,
    isAdmin,
    canUploadZatca,
    onViewOrder,
    onPrintInvoice,
    onEditOrder,
    onDeleteOrder,
    onReturnOrder,
    onUploadZatca
}) => {
    const { t, language } = useTranslation();
    if (loading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
            </div>
        );
    }

    if (orders.length === 0) {
        return (
            <div className="text-center py-12">
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {t('admin.no_orders')}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                    {t('admin.no_orders_found')}
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className={`px-6 py-3 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('admin.order_number')}
                            </th>
                            <th className={`px-6 py-3 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('admin.date')}
                            </th>
                            <th className={`px-6 py-3 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('admin.customer')}
                            </th>
                            
                            <th className={`px-6 py-3 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('admin.filters.order_type')}
                            </th>

                            <th className={`px-6 py-3 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('admin.total')}
                            </th>
                            <th className={`px-6 py-3 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('admin.status')}
                            </th>
                            <th className={`px-6 py-3 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('admin.uploaded')}
                            </th>
                            <th className={`px-6 py-3 ${language === 'ar' ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                {t('admin.actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {orders.map((order) => (
                            <tr key={order.unique_id || `${order.payment_method}-${order.id}`} className="hover:bg-gray-50 transition-colors duration-200">
                                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    #{order.order_number}
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {formatDate(order.created_at, language)}
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {order.customer_name || t('admin.walk_in_customer')}
                                    {order.customer_phone && (
                                        <div className="text-gray-500 text-xs">{order.customer_phone}</div>
                                    )}
                                </td>
                                
                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {order.is_card_order ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {t('admin.filters.card')}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            {t('admin.filters.cash')}
                                        </span>
                                    )}
                                </td>

                                <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {formatCurrency(order.total)}
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {order.status === 'returned' ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            {t('admin.returned')}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            {t('admin.active')}
                                        </span>
                                    )}
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap ${language === 'ar' ? 'text-right' : 'text-left'} text-sm font-medium`}>
                                    {order.is_zatca_uploaded ? (
                                        <span className="text-green-600" title="Uploaded to ZATCA">
                                            <FaCheckCircle className="w-5 h-5" />
                                        </span>
                                    ) : (
                                        <span className="text-red-500" title="Not uploaded to ZATCA">
                                            <FaTimesCircle className="w-5 h-5" />
                                        </span>
                                    )}
                                </td>
                                <td className={`px-6 py-4 whitespace-nowrap ${language === 'ar' ? 'text-left' : 'text-right'} text-sm font-medium`}>
                                    <button
                                        onClick={() => onViewOrder(order)}
                                        className="text-yellow-600 hover:text-yellow-900 mx-2"
                                        title={t('admin.view_order')}
                                    >
                                        <FaEye className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => onPrintInvoice(order)}
                                        className="text-yellow-600 hover:text-yellow-900 mx-2"
                                        title={t('admin.print_invoice')}
                                    >
                                        <FaPrint className="w-5 h-5" />
                                    </button>
                                    {/* ZATCA upload action */}
                                    {!order.is_zatca_uploaded && canUploadZatca && (
                                        <button
                                            onClick={() => onUploadZatca(order)}
                                            className="text-yellow-600 hover:text-yellow-900 mx-2"
                                            title="Upload to ZATCA"
                                        >
                                            <FaCloudUploadAlt className="w-5 h-5" />
                                        </button>
                                    )}
                                    {isAdmin && (
                                        <>
                                            <button
                                                onClick={() => onEditOrder(order)}
                                                className="text-yellow-600 hover:text-yellow-900 mx-2"
                                                title={t('admin.edit_order')}
                                            >
                                                <FaEdit className="w-5 h-5" />
                                            </button>
                                            {!order.status && (
                                                <button
                                                    onClick={() => onReturnOrder(order)}
                                                    className="text-yellow-600 hover:text-yellow-900 mx-2"
                                                    title={t('admin.return_order')}
                                                >
                                                    <FaUndo className="w-5 h-5" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => onDeleteOrder(order)}
                                                className="text-yellow-600 hover:text-yellow-900 mx-2"
                                                title={t('admin.delete_order')}
                                            >
                                                <FaTrash className="w-5 h-5" />
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default OrdersTable;
